<template>
  <DocumentCard class="component-wrapper" :data="reference" :index="index" @title-clicked="openReferenceDialog">
    <ExpansionPanel
      v-if="reference.pageNumber?.length"
      v-model="isExpanded"
      class="referenced-panel"
      :title="referencedPanelTitle"
    >
      <div class="referenced-pages">
        <div class="image-container" v-for="page in reference.pageNumber" :key="page">
          <BaseImage class="reference-thumb" :src="getThumbSource(page)" alt="thumb" @click="previewDocument(page)" />
          <span class="page-index">{{ t('common.page') }} #{{ page }}</span>
        </div>
      </div>
    </ExpansionPanel>
  </DocumentCard>
  <WarningPopup
    :popup-opened="missingCollectionPopupOpened"
    title-key="errors.somethingWentWrong"
    warning-message-key="collections.collectionNotFound"
    @update:open="missingCollectionPopupOpened = false"
    @execute:action="missingCollectionPopupOpened = false"
  />
</template>

<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { CURRENT_DIALOG } from '~/constants/conversationModels';

import useDocuments from '~/composables/useDocuments';
import useModals from '~/composables/useModals';
import ExpansionPanel from '~/components/common/ExpansionPanel';
import useCollections from '~/composables/useCollections';
import WarningPopup from '~/components/common/WarningPopup';
import DocumentCard from '~/components/conversation/chatMessage/DocumentCard';
import validateUrl from '~/utils/url';
import BaseImage from '~/components/common/BaseImage.vue';

export default {
  name: 'ReferenceCard',
  components: { BaseImage, DocumentCard, ExpansionPanel, WarningPopup },
  props: {
    index: { type: Number, default: 0 },
    reference: { type: Object, default: () => ({}) },
    collectionId: { type: String, default: '' },
    collectionName: { type: String, default: '' },
  },
  setup(props) {
    const { t } = useI18n();
    const { showPreviewDocument, setCurrentDocumentsDialog } = useModals();
    const { setSelectedDocument, getDocumentThumbs } = useDocuments();
    const { openModalInCollectionScope, currentCollections } = useCollections();

    const isExpanded = ref(false);
    const missingCollectionPopupOpened = ref(false);
    const reactivePages = ref({});

    const referencedPanelTitle = computed(
      () =>
        `${t('common.view')} ${props.reference.pageNumber?.length || 0} ${t('conversation.referenceCard.sourcePages')}`,
    );
    const isAvailableCollection = computed(() =>
      currentCollections.value.some((c) => c.collectionId === props.collectionId),
    );

    const getThumbSource = (pageNumber) =>
      validateUrl(reactivePages.value?.[pageNumber]?.small) ? reactivePages.value[pageNumber].small : '';

    const fetchThumbs = async () => {
      if (!props.reference.externalId || !props.reference.pageNumber?.length) return;
      try {
        const response = await getDocumentThumbs({
          documents: [{ id: props.reference.externalId, pages: props.reference.pageNumber }],
        });
        const docThumbs = response[0]?.pages || {};
        reactivePages.value = Object.fromEntries(Object.entries(docThumbs).map(([k, v]) => [Number(k), v]));
      } catch (err) {
        console.error('Error fetching thumbs', err);
      }
    };

    const openReferenceDialog = async () => {
      // open document in collection scope
      if (props.collectionId && props.collectionId !== 'vault') {
        if (!isAvailableCollection.value) {
          missingCollectionPopupOpened.value = true;
          return;
        }
        await openModalInCollectionScope(props.collectionId, props.collectionName, props.reference);
      } else {
        setCurrentDocumentsDialog(CURRENT_DIALOG.document_panel);
        await setSelectedDocument({ reference: props.reference });
      }
    };

    const previewDocument = async (page) => {
      await setSelectedDocument({ reference: props.reference });
      showPreviewDocument(page);
    };

    // Reset thumbnails if reference changes
    watch(
      () => props.reference.externalId,
      () => {
        reactivePages.value = {};
        isExpanded.value = false;
      },
    );

    // Fetch thumbnails each time panel expands
    watch(isExpanded, async (val) => {
      if (val) {
        await fetchThumbs();
      }
    });

    return {
      isExpanded,
      reactivePages,
      referencedPanelTitle,
      getThumbSource,
      openReferenceDialog,
      previewDocument,
      missingCollectionPopupOpened,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/styles/variables';

.component-wrapper {
  .referenced-panel {
    :deep(.v-expansion-panel) {
      background-color: $bb-neutral-0;
      padding: 0;
    }

    :deep(.v-expansion-panel-title) {
      width: auto;
      margin-left: 130px;
      margin-top: -24px;
    }

    :deep(.body-2) {
      @include bb-regular-text-styles('md');
    }

    :deep(.referenced-pages) {
      display: flex;
      gap: $bb-spacing-4;
      flex-wrap: wrap;
      margin-top: $bb-spacing-2;
    }

    :deep(.image-container) {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: $bb-spacing-2;

      .image-wrapper {
        & .v-skeleton-loader {
          height: 58px;
        }
      }

      img {
        max-width: 80px;
        max-height: 80px;
        align-self: flex-start;
        cursor: pointer;
        padding: $bb-spacing-2;
        border-radius: $bb-spacing-1;
        background-color: $bb-neutral-5;
      }

      .page-index {
        @include bb-medium-text-styles('sm');
        color: $bb-neutral-1;
      }
    }
  }
}
</style>
